import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ScreenContent from "../../../../components/layout/ScreenContent";
import TitledHeaderWithSearch from "../../../../components/layout/TitledHeaderWithSearch";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";

import { isNil } from "lodash";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import { useDrilldownBaseUrl } from "../../../../pipelines/PipelineContext";
import {
  loanFetchedDataFundingEntitiesSet,
  loanFetchedDataLoanTermsSet,
} from "../../../../redux/reducers/loanFetchedDataReducer";
import LoanFeesSubTabBrokerTerms from "./tabs/LoanFeesSubTabBrokerTerms";
import LoanFeesSubTabCorrespondentFees from "./tabs/LoanFeesSubTabCorrespondentFees";
import LoanFeesSubTabEscrowItems from "./tabs/LoanFeesSubTabEscrowItems";
import LoanFeesSubTabFundingShield from "./tabs/LoanFeesSubTabFundingShield";
import LoanFeesSubTabOriginatorCredits from "./tabs/LoanFeesSubTabOriginatorCredits";
import LoanFeesSubTabOriginatorFees from "./tabs/LoanFeesSubTabOriginatorFees";
import LoanFeesSubTabTermsSheet from "./tabs/LoanFeesSubTabTermsSheet";
import LoanFeesSubTabThirdPartyFees from "./tabs/LoanFeesSubTabThirdPartyFees";
import LoanFeesSubTabWireInstructions from "./tabs/LoanFeesSubTabWireInstructions";

import { CircularProgress } from "@mui/material";
import { Navigate, Route, Routes } from "react-router-dom";
import Flx from "../../../../components/layout/Flx";
import ToggleRouterTabSwitcher from "../../../../components/navigation/ToggleRouterTabSwitcher";
import LoanFeesSubTabLetterOfIntent from "./tabs/LoanFeesSubTabLetterOfIntent";

const LoanDrilldownFeesTab = ({}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const baseUrl = useDrilldownBaseUrl(loanDrilldown?._id);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const fundingEntities = useSelector(
    (state) => state?.loanFetchedData?.fundingEntities
  );

  const dispatch = useDispatch();
  const { getLoanTerms, getFundingEntities } = useLoanTermsHook();

  const tabsList = useMemo(() => {
    return {
      LOAN_TERMS: "loan-terms",
      // LETTER_OF_INTENT: "letter-of-intent",
      ORIGINATOR_FEES: "originator-fees",
      ORIGINATOR_CREDITS: "originator-credits",
      THIRD_PARTY_FEES: "third-party-fees",
      CORRESPONDENT_FEES: "correspondent-fees",
      BROKER_TERMS: "broker-terms",
      ESCROW: "escrow",
      WIRE_INSTRUCTIONS: "wire-instructions",
      FUNDING_SHIELD: "funding-shield",
    };
  }, []);
  const defaultTabKey = tabsList.LOAN_TERMS;

  const routerTabs = useMemo(() => {
    function kebabToSentence(str = "") {
      return String(str)
        .trim()
        .split("-")
        .filter(Boolean) // remove empty parts from leading/trailing/multiple dashes
        .join(" ")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase());
    }
    return Object.keys(tabsList)?.map((t) => {
      return {
        label: kebabToSentence(tabsList[t]),
        to: `${baseUrl}/fees-and-closing/${tabsList[t]}`,
      };
    });
  }, [baseUrl, tabsList]);

  useEffect(() => {
    if (isNil(loanTerms)) {
      getLoanTerms({
        dealId: loanDrilldown?._id,
        onSuccessFn: (data) => {
          dispatch(loanFetchedDataLoanTermsSet(data));
        },
        onFailFn: () => {
          dispatch(loanFetchedDataLoanTermsSet({}));
        },
      });
    }
  }, [loanTerms]);

  useEffect(() => {
    if (isNil(fundingEntities)) {
      getFundingEntities({
        dealId: loanDrilldown?._id,
        onSuccessFn: (data) => {
          dispatch(loanFetchedDataFundingEntitiesSet(data));
        },
        onFailFn: () => {
          dispatch(loanFetchedDataFundingEntitiesSet({}));
        },
      });
    }
  }, [fundingEntities]);

  return (
    <LoanDrilldownTabPanel>
      <TitledHeaderWithSearch
        title={"Fees & Closing"}
        sx={{ pb: 0.2 }}
        borderColor={"#eeeeee"}
      >
        <ToggleRouterTabSwitcher
          sx={{ ml: -1 }}
          variant={"underline"}
          tabs={routerTabs}
        />
      </TitledHeaderWithSearch>

      {isNil(loanTerms) ? (
        <Flx center sx={{ p: 7 }}>
          <CircularProgress />
        </Flx>
      ) : (
        <ScreenContent
          sx={{
            flexGrow: 1,
            width: "100%",
            margin: "0px auto",
          }}
        >
          <Routes>
            <Route
              index
              element={<Navigate to={tabsList.LOAN_TERMS} replace />}
              // element={<Navigate to={tabsList[defaultTabKey]} replace />}
            />

            <Route
              path={tabsList.LOAN_TERMS}
              element={<LoanFeesSubTabTermsSheet />}
            />

            <Route
              path={"letter-of-intent"}
              // path={tabsList.LETTER_OF_INTENT}
              element={<LoanFeesSubTabLetterOfIntent />}
            />
            <Route
              path={tabsList.ORIGINATOR_FEES}
              element={<LoanFeesSubTabOriginatorFees />}
            />
            <Route
              path={tabsList.ORIGINATOR_CREDITS}
              element={<LoanFeesSubTabOriginatorCredits />}
            />
            <Route
              path={tabsList.THIRD_PARTY_FEES}
              element={<LoanFeesSubTabThirdPartyFees />}
            />
            <Route
              path={tabsList.CORRESPONDENT_FEES}
              element={<LoanFeesSubTabCorrespondentFees />}
            />
            <Route
              path={tabsList.BROKER_TERMS}
              element={<LoanFeesSubTabBrokerTerms />}
            />
            <Route
              path={tabsList.ESCROW}
              element={<LoanFeesSubTabEscrowItems />}
            />
            <Route
              path={tabsList.WIRE_INSTRUCTIONS}
              element={<LoanFeesSubTabWireInstructions />}
            />
            <Route
              path={tabsList.FUNDING_SHIELD}
              element={<LoanFeesSubTabFundingShield />}
            />

            {/* Fallback for unknown slugs */}
            <Route
              path="*"
              element={<Navigate to={tabsList[defaultTabKey]} replace />}
            />
          </Routes>
        </ScreenContent>
      )}
    </LoanDrilldownTabPanel>
  );
};

export default LoanDrilldownFeesTab;
